import { useState, useMemo } from "react";
import { Input } from "../components/ui/input";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { Search } from "lucide-react";
import Seo from "../components/Seo";

const ALL_FAQ = [
    {
        q: "Muss ich das Pflegeheim meiner Eltern bezahlen?",
        a: "In vielen Fällen nicht. Nach dem Angehörigen-Entlastungsgesetz (§ 94 Abs. 1a SGB XII) wird das Einkommen von Kindern grundsätzlich erst ab mehr als 100.000 € Jahresbrutto für Unterhaltsforderungen des Sozialamts herangezogen.",
    },
    {
        q: "Was zählt zur 100.000-€-Grenze?",
        a: "Maßgeblich ist das Jahresbrutto je Kind. Einnahmen aus Kapitalvermögen oder Vermietung können eine Rolle spielen; die konkrete Berechnung kann komplex sein und sollte individuell geprüft werden.",
    },
    {
        q: "Reicht die Rente meiner Eltern nicht – muss ich einspringen?",
        a: "Erst einmal werden Leistungen der Pflegekasse, Rente und Vermögen der pflegebedürftigen Person herangezogen. Reichen diese nicht, kommt Hilfe zur Pflege (Sozialhilfe) in Betracht. Kinder werden grundsätzlich nur oberhalb der 100.000-€-Grenze einbezogen.",
    },
    {
        q: "Was kostet ein Pflegeheim in Baden-Württemberg?",
        a: "Der Gesamt-Eigenanteil gehört zu den höchsten in Deutschland. Er setzt sich aus pflegebedingtem Eigenanteil, Unterkunft & Verpflegung sowie Investitionskosten zusammen und variiert je Einrichtung.",
    },
    {
        q: "Zahlen Geschwister gleich viel?",
        a: "Nein. Eine pauschale Gleichverteilung gibt es nicht. Relevant sind u. a. Einkommen, Unterhaltspflichten gegenüber eigenen Kindern und weitere individuelle Faktoren.",
    },
    {
        q: "Welche Rolle spielt der Pflegegrad?",
        a: "Der Pflegegrad bestimmt wesentliche Leistungen der Pflegekasse im Heim. Je höher der Pflegegrad, desto höher typischerweise die Leistungen – der pflegebedingte Eigenanteil bleibt innerhalb einer Einrichtung jedoch meist gleich.",
    },
    {
        q: "Was ist Hilfe zur Pflege?",
        a: "Sozialhilfeleistung nach SGB XII, die einspringt, wenn eigene Mittel und Pflegekassenleistungen für die Heimkosten nicht ausreichen. Das Sozialamt prüft Einkommen und Vermögen.",
    },
    {
        q: "Sind Ihre Hinweise rechtlich verbindlich?",
        a: "Nein. Wir liefern Orientierung und Struktur. Für Ihre individuelle Situation vermitteln wir erfahrene Ansprechpartner.",
    },
    {
        q: "Wie sicher sind meine Daten?",
        a: "Ihre Angaben werden vertraulich verarbeitet und nicht weitergegeben. Sie haben jederzeit das Recht auf Auskunft, Berichtigung und Löschung nach DSGVO.",
    },
];

export default function FAQPage() {
    const [q, setQ] = useState("");
    const filtered = useMemo(() => {
        if (!q.trim()) return ALL_FAQ;
        const needle = q.toLowerCase();
        return ALL_FAQ.filter(
            (i) => i.q.toLowerCase().includes(needle) || i.a.toLowerCase().includes(needle),
        );
    }, [q]);

    return (
        <div className="py-12 sm:py-16">
            <Seo
                title="FAQ Pflegeheimkosten für Angehörige"
                description="Häufige Fragen rund um Pflegeheimkosten, Elternunterhalt und das Angehörigen-Entlastungsgesetz. Kurze, verständliche Antworten zu 100.000-€-Grenze, Eigenanteil, Geschwistern und Hilfe zur Pflege."
                path="/faq"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": ALL_FAQ.map((f) => ({
                        "@type": "Question",
                        "name": f.q,
                        "acceptedAnswer": { "@type": "Answer", "text": f.a },
                    })),
                }}
            />
            <div className="container max-w-3xl">
                <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">FAQ</div>
                <h1 className="mt-2 font-display text-4xl font-bold tracking-tight sm:text-5xl">
                    Häufige Fragen – kurz beantwortet.
                </h1>
                <p className="mt-3 text-ink-500">Alle Antworten ersetzen keine individuelle Rechtsberatung.</p>

                <div className="relative mt-6">
                    <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
                    <Input
                        placeholder="FAQ durchsuchen…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="h-12 rounded-full pl-10"
                        data-testid="faq-search"
                    />
                </div>

                <Accordion type="single" collapsible className="mt-6">
                    {filtered.map((f, i) => (
                        <AccordionItem key={f.q} value={`i-${i}`} data-testid={`faq-item-${i}`}>
                            <AccordionTrigger className="text-left font-display text-base font-semibold">
                                {f.q}
                            </AccordionTrigger>
                            <AccordionContent className="text-sm text-ink-500">{f.a}</AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </div>
    );
}
